import { Inject, Injectable } from '@angular/core'
import { Router, Params, ParamMap } from '@angular/router'
import { map, shareReplay, switchMap, take } from 'rxjs/operators'
import { iif, Observable, of } from 'rxjs'
import { ProductService } from './product.service'
import { EnvironmentService } from '../../singleton-services/environment.service'
import { LocationService } from '../../singleton-services/location.service'
import {
  buildTrackWrestlingCreateAccountURL,
  buildTrackWrestlingLoginURL,
  ensureUrlHttps
} from './external-routing.utility'
import { ExternalRoutingService } from './external-routing.service'
import { LiveEvent } from '@flocasts/flosports30-types/dist/entity'
import { shouldRouteToPlansOrPay } from 'src/app/funnel/navigation/navigation.utility'
import { WINDOW } from 'src/app/app.injections'
import { ExperimentationService } from '../experimentation/experimentation.service'
import { FEAT_PRICING_ARCH_ID, FEAT_PRICING_ARCH_VARIATION } from '../experimentation/experiment.model'
import { LiveEventStatus } from '../../events/events.enum'

export interface IFinalNavigation {
  url: string
  isExternal: boolean
  type: 'redirect' | 'go' | 'next'
}

export interface ICheckoutNavigationService {
  createAccountStepsText$: Observable<string>
  plansStepsText$: Observable<string>
  payStepsText$: Observable<string>

  navigateAfterLandingPage(
    queryParams: ParamMap,
    isInNewPricingArchVariation: boolean,
    liveEventRedirect?: { status: LiveEventStatus; eventId: number }
  ): void

  navigateAfterCreatingAccount(queryParams: Params, liveEvent: LiveEvent): void

  navigateAfterLogin(userIsLoggedIn: boolean, isFreeUser: boolean, queryParams: Params, liveEvent?: LiveEvent): void

  navigateToPlansOrPay(queryParams: Params): void

  navigateToFinalDestination(queryParams: Params): void
}

@Injectable()
export class CheckoutNavigationService implements ICheckoutNavigationService {
  public isInNewPricingArchitectureVariation$ = this.experimentService.isInVariation(
    FEAT_PRICING_ARCH_ID,
    FEAT_PRICING_ARCH_VARIATION,
    false
  )

  public skipPlanSelection$ = this.productService.hasOneShownPlan$.pipe(
    map(hasOneShownPlan => hasOneShownPlan),
    shareReplay(1)
  )

  /**
   * If user is in new Pricing Architecture experiment, products will not be called.
   */
  public numberOfCheckoutSteps$ = this.isInNewPricingArchitectureVariation$.pipe(
    switchMap(isInVariation => iif(() => isInVariation, of(false), this.skipPlanSelection$)),
    map(getNumberOfSteps),
    shareReplay(1)
  )

  public routeToPlansOrPay$ = this.isInNewPricingArchitectureVariation$.pipe(
    switchMap(isInVariation => iif(() => isInVariation, of(false), this.skipPlanSelection$)),
    map(getRouteToPlansOrPay),
    shareReplay(1)
  )

  public createAccountStepsText$ = this.numberOfCheckoutSteps$.pipe(
    map(numberOfSteps => `Step 1 of ${numberOfSteps}`),
    shareReplay(1)
  )
  public plansStepsText$ = this.numberOfCheckoutSteps$.pipe(
    map(numberOfSteps => {
      return numberOfSteps < 3 ? '' : `Step ${numberOfSteps - 1} of ${numberOfSteps}`
    }),
    shareReplay(1)
  )
  public payStepsText$ = this.numberOfCheckoutSteps$.pipe(
    map(numberOfSteps => `Step ${numberOfSteps} of ${numberOfSteps}`),
    shareReplay(1)
  )

  public navigateAfterLandingPage(
    queryParams: Params,
    isInNewPricingArchVariation: boolean,
    liveEventRedirect?: { status: LiveEventStatus; eventId: number }
  ): void {
    // If live event is PreAir or Concluded, redirect to event hub
    const qp =
      liveEventRedirect && liveEventRedirect.status !== LiveEventStatus.Live
        ? { ...queryParams, redirect: `/events/${liveEventRedirect.eventId}` }
        : queryParams

    if (isInNewPricingArchVariation) {
      this.router.navigate(['/account/change-offer/plans'], { queryParams: qp, queryParamsHandling: 'merge' })
    } else {
      this.router.navigate(['/create-account'], { queryParams: qp, queryParamsHandling: 'merge' })
    }
  }

  public navigateAfterCreatingAccount(queryParams: Params, liveEvent?: LiveEvent): void {
    const shouldSkipPlans = !shouldRouteToPlansOrPay(true, queryParams, liveEvent)
    if (shouldSkipPlans) {
      this.navigateToFinalDestination(queryParams)
    } else {
      this.router.navigate(['plans'], { queryParams })
    }
  }

  public navigateAfterLogin(userIsLoggedIn: boolean, isFreeUser: boolean, queryParams: Params, liveEvent?: LiveEvent) {
    // userIsLoggedIn ensures that if this were to somehow get invoked with an anonymous user,
    // they will never get routed to the plans or pay page.
    if (userIsLoggedIn && shouldRouteToPlansOrPay(isFreeUser, queryParams, liveEvent)) {
      this.navigateToPlansOrPay(queryParams)
    } else {
      this.navigateToFinalDestination(queryParams)
    }
  }

  public navigateToPlansOrPay(queryParams: Params) {
    this.routeToPlansOrPay$.pipe(take(1)).subscribe(route => {
      this.router.navigate([route], { queryParams })
    })
  }

  public navigateToFinalDestination(queryParams: Params) {
    const finalDestination = determineFinalDestinationFromQueryParams(queryParams)
    finalDestination.isExternal
      ? (this.window.location.href = finalDestination.url)
      : this.router.navigateByUrl(finalDestination.url)
  }

  public redirectTrackWrestlingUsersForCreateAccount(next: string | undefined): void {
    const redirectUrl = ensureUrlHttps(`${this.ls.location}/plans`)
    const destination = buildTrackWrestlingCreateAccountURL(redirectUrl, this.environmentService.config.env, next)
    this.externalRoutingService.redirectUser(destination)
  }

  public redirectTrackWrestlingUsersForLogin(): void {
    const destination = buildTrackWrestlingLoginURL(this.environmentService.config.env)
    this.externalRoutingService.redirectUser(destination)
  }

  constructor(
    private router: Router,
    private readonly productService: ProductService,
    private readonly externalRoutingService: ExternalRoutingService,
    private readonly environmentService: EnvironmentService,
    private readonly ls: LocationService,
    @Inject(WINDOW) private readonly window: Window,
    private readonly experimentService: ExperimentationService
  ) {}
}

const getNumberOfSteps = (skipPlanSelection?: boolean) => (skipPlanSelection ? 2 : 3)
const getRouteToPlansOrPay = (skipPlanSelection?: boolean) => (skipPlanSelection ? 'pay' : 'plans')

/* checks for the redirect= queryParam starting with /live/ */
const redirectIsLive = (redirect: string) => redirect.match(/^\/live.*$/)

const redirectIsMileSplit = (redirect?: string) => redirect && redirect.includes('http')

/**
 * We have three different query params for redirecting users
 * 1. redirect - used with paywalls/premium content and when detected, high probability user will be redirected to plan or pay page
 * see shouldRouteToPlansOrPay
 * 2. next - used mainly for redirecting to external sites
 * 3. go - used for redirecting within the app to non-premium content
 * ex: When a user is on the events page and clicks login button in the header,
 * we set ?go=/events so they can be returned there after logging in
 * @param queryParams The query params in the url
 */
const determineFinalDestinationFromQueryParams = (queryParams: Params): IFinalNavigation => {
  const redirectUrl = queryParams.redirect
  const nextUrl = queryParams.next
  const goUrl = queryParams.go

  if (!!redirectUrl && redirectIsLive(redirectUrl)) {
    return {
      url: redirectUrl,
      isExternal: false,
      type: 'redirect'
    }
  }

  if (!!nextUrl || redirectIsMileSplit(redirectUrl)) {
    return {
      url: nextUrl || redirectUrl,
      isExternal: true,
      type: nextUrl ? 'next' : 'redirect'
    }
  }

  return {
    url: redirectUrl || goUrl || '/',
    isExternal: false,
    type: redirectUrl ? 'redirect' : 'go'
  }
}

/* parses the /live/43101-some-slug/ redirect queryParam to return only 43101 */
export const getLiveIdFromRedirectQueryParam = (redirect: string): number | undefined => {
  if (redirectIsLive(redirect)) {
    const parsed = redirect.split('/')[2]
    return +parsed.split('-')[0]
  }

  return undefined
}
